import type {
  CreateGiftCardBatchRequest,
  CreateGiftCardBatchResponse,
  GetGiftCardResponse,
  IssueGiftCardRequest,
  IssueGiftCardResponse,
  ListGiftCardBatchesResponse,
  ListGiftCardsQuery,
  ListGiftCardsResponse,
  RedeemGiftCardOnBehalfResponse,
  RevokeGiftCardResponse,
  UpdateGiftCardBatchStatusResponse,
} from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers around `ops-api /v1/ops/gift-cards/*`.
 *
 * Contract:
 *   - List endpoints feed batch operations and audit-style filtering.
 *   - Code lookup remains the canonical operator path for a single card.
 *   - `actor_user_id` is auto-injected by ops-api from the session
 *     cookie; clients never set it. The redeem-on-behalf endpoint also
 *     auto-generates `external_request_id` if the client omits it,
 *     but we still send a fresh UUID so retries dedupe correctly.
 */

const BASE_PATH = '/v1/ops/gift-cards';

function giftCardListSearch(query: ListGiftCardsQuery): string {
  const params = new URLSearchParams();
  if (query.batch_id !== undefined && query.batch_id.length > 0) {
    params.set('batch_id', query.batch_id);
  }
  if (query.status !== undefined) {
    params.set('status', query.status);
  }
  if (query.account_id !== undefined && query.account_id.length > 0) {
    params.set('account_id', query.account_id);
  }
  if (query.from !== undefined && query.from.length > 0) {
    params.set('from', query.from);
  }
  if (query.to !== undefined && query.to.length > 0) {
    params.set('to', query.to);
  }
  if (query.limit !== undefined) {
    params.set('limit', String(query.limit));
  }
  if (query.offset !== undefined) {
    params.set('offset', String(query.offset));
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export async function listGiftCards(
  query: ListGiftCardsQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListGiftCardsResponse> {
  return opsApiRequest<ListGiftCardsResponse>(
    `${BASE_PATH}${giftCardListSearch(query)}`,
    { signal },
    fetchImpl,
  );
}

export async function listGiftCardBatches(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListGiftCardBatchesResponse> {
  return opsApiRequest<ListGiftCardBatchesResponse>(
    `${BASE_PATH}/batches`,
    { signal },
    fetchImpl,
  );
}

export async function createGiftCardBatch(
  body: CreateGiftCardBatchRequest,
  fetchImpl: typeof fetch,
): Promise<CreateGiftCardBatchResponse> {
  return opsApiRequest<CreateGiftCardBatchResponse>(
    `${BASE_PATH}/batches`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function closeGiftCardBatch(
  id: string,
  fetchImpl: typeof fetch,
): Promise<UpdateGiftCardBatchStatusResponse> {
  return opsApiRequest<UpdateGiftCardBatchStatusResponse>(
    `${BASE_PATH}/batches/${encodeURIComponent(id)}/close`,
    { method: 'POST' },
    fetchImpl,
  );
}

export async function reopenGiftCardBatch(
  id: string,
  fetchImpl: typeof fetch,
): Promise<UpdateGiftCardBatchStatusResponse> {
  return opsApiRequest<UpdateGiftCardBatchStatusResponse>(
    `${BASE_PATH}/batches/${encodeURIComponent(id)}/reopen`,
    { method: 'POST' },
    fetchImpl,
  );
}

export async function issueGiftCard(
  body: IssueGiftCardRequest,
  fetchImpl: typeof fetch,
): Promise<IssueGiftCardResponse> {
  return opsApiRequest<IssueGiftCardResponse>(
    `${BASE_PATH}/issue`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function getGiftCard(
  code: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetGiftCardResponse> {
  return opsApiRequest<GetGiftCardResponse>(
    `${BASE_PATH}/${encodeURIComponent(code)}`,
    { signal },
    fetchImpl,
  );
}

/**
 * Revoke a gift card. `actor_user_id` is overridden by ops-api from the
 * session, so the client only sends `reason` (optional).
 */
export interface RevokeGiftCardClientRequest {
  readonly reason?: string;
}

export async function revokeGiftCard(
  code: string,
  body: RevokeGiftCardClientRequest,
  fetchImpl: typeof fetch,
): Promise<RevokeGiftCardResponse> {
  return opsApiRequest<RevokeGiftCardResponse>(
    `${BASE_PATH}/${encodeURIComponent(code)}/revoke`,
    { method: 'POST', body },
    fetchImpl,
  );
}

/**
 * Redeem on behalf. ops-api ignores client-supplied `actor_user_id` and
 * substitutes the session opsUserId, so we only send the operator-chosen
 * fields. We always set `external_request_id` ourselves so a button-click
 * replay dedupes idempotently downstream.
 */
export interface RedeemOnBehalfClientRequest {
  readonly code: string;
  readonly account_id: string;
  readonly external_request_id: string;
  readonly reason?: string;
  readonly metadata?: Record<string, unknown>;
}

export async function redeemOnBehalf(
  body: RedeemOnBehalfClientRequest,
  fetchImpl: typeof fetch,
): Promise<RedeemGiftCardOnBehalfResponse> {
  return opsApiRequest<RedeemGiftCardOnBehalfResponse>(
    `${BASE_PATH}/redeem-on-behalf`,
    { method: 'POST', body },
    fetchImpl,
  );
}

